import { Box, Chip, LinearProgress, Skeleton, Typography, useTheme } from '@mui/material';
import CustomCard from 'components/shared/CustomCard/CustomCard';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import formatCurrency from 'helper/short-currency';
import { ClientType } from 'model/client.model';
import zeplinColor from 'theme/Color';
import {
  getClientHealthScore,
  getClientStatusLabel,
  getHealthBarColor,
  getStatusChipColor,
} from './clientHealth';
import { getCategoryChipColor } from './utilities/clientCategory';

const CustomClientCard = ({ client, loading }: { client: ClientType; loading: boolean }) => {
  const theme = useTheme();
  const clientStatus = getClientStatusLabel(client);
  const healthScore = getClientHealthScore(client);
  const normalizedCategoryLabel =
    client?.category === 'Business_Development' ? 'Business Development' : client?.category || 'NA';
  const categoryChipColor = getCategoryChipColor(client?.category);
  const primaryMetricLabel = client.primaryMetricLabel || 'Open Deals';
  const secondaryMetricLabel = client.secondaryMetricLabel || 'Close Deals';
  const primaryMetricValue = client.primaryMetricValue ?? client.openDealAmount;
  const secondaryMetricValue = client.secondaryMetricValue ?? client.closedDealAmount;
  const tertiaryMetricLabel = client.tertiaryMetricLabel;
  const tertiaryMetricValue = client.tertiaryMetricValue;
  const hasCustomPrimaryMetric = Boolean(client.primaryMetricLabel);
  const hasCustomSecondaryMetric = Boolean(client.secondaryMetricLabel);
  const hasCustomTertiaryMetric = Boolean(client.tertiaryMetricLabel);

  const metricItems = [
    {
      label: primaryMetricLabel,
      value: primaryMetricValue,
      isCustomMetric: hasCustomPrimaryMetric,
    },
    {
      label: secondaryMetricLabel,
      value: secondaryMetricValue,
      isCustomMetric: hasCustomSecondaryMetric,
    },
    ...(tertiaryMetricLabel
      ? [
          {
            label: tertiaryMetricLabel,
            value: tertiaryMetricValue,
            isCustomMetric: hasCustomTertiaryMetric,
          },
        ]
      : []),
  ];

  const renderMetricValue = (
    value: string | number | null | undefined,
    label: string,
    isCustomMetric: boolean,
  ) => {
    if (value === null || value === undefined || value === '') return 'N/A';

    if (!isCustomMetric) return `$${value}`;

    if (label.toLowerCase().includes('revenue')) {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? `$${formatCurrency(numericValue)}` : String(value);
    }

    return String(value);
  };

  return (
    <>
      <CustomCard
        styles={{
          borderRadius: '14px',
          border: `1px solid ${zeplinColor.Neutral5}`,
          '&:hover': {
            borderColor: zeplinColor.BasicBlack,
          },
          padding: '16px',
          gap: '12px',
          display: 'flex',
          flexDirection: 'column',
          minWidth: '277px',
          cursor: 'pointer',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          {client?.avatar ? (
            <img
              src={client?.avatar}
              alt='client-avatar'
              style={{
                position: 'relative',
                objectFit: 'fill',
                height: '82px',
                borderRadius: '14px 14px 14px 14px',
                border: `1px solid ${zeplinColor.Neutral3}`,
                width: '82px',
              }}
            />
          ) : (
            <Box
              sx={{
                backgroundColor:
                  client?.category === 'Talent'
                    ? zeplinColor.Brand1
                    : loading
                      ? zeplinColor.Neutral3
                      : client?.category === 'Athlete'
                        ? zeplinColor.USER_PROFILE_COLOR1
                        : client?.category === 'Business'
                          ? zeplinColor.USER_PROFILE_COLOR2
                          : client?.category === 'Business_Development'
                            ? zeplinColor.USER_PROFILE_COLOR3
                            : zeplinColor.Brand1,
                borderRadius: '14px 14px 14px 14px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: '82px',
                maxWidth: '82px',
                height: '82px',
              }}
            >
              <Typography
                variant='display4'
                sx={{
                  color: zeplinColor.Neutral1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {client?.Name?.split(' ').map((val, index) =>
                  index === 0 || index === client?.Name?.split(' ').length - 1 ? val[0] : '',
                )}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  flex: 1,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '8px',
                  }}
                >
                  <LightTooltip
                    title={
                      <Typography variant='body3' color={zeplinColor.Neutral1}>
                        {client?.Name || 'N/A'}
                      </Typography>
                    }
                    placement='bottom-start'
                    style={{
                      backgroundColor: zeplinColor.BasicBlack,
                      borderRadius: '8px',
                      position: 'relative',
                    }}
                  >
                    <Typography
                      variant='body1'
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        maxWidth: 'calc(100% - 10px)',
                        textOverflow: 'ellipsis',
                        width: '100%',
                        boxSizing: 'border-box',
                        flex: 1,
                      }}
                    >
                      {client?.Name || 'N/A'}
                    </Typography>
                  </LightTooltip>

                  <Chip
                    label={clientStatus}
                    sx={{
                      backgroundColor: getStatusChipColor(clientStatus),
                      color: zeplinColor.Neutral1,
                      borderRadius: '50px',
                      height: '24px',
                      flexShrink: 0,
                      '& .MuiChip-label': {
                        px: '10px',
                      },
                    }}
                  />
                </Box>
              </Box>
            )}
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  flex: 1,
                }}
              />
            ) : (
              <Box sx={{ display: 'inline-flex', gap: '4px', width: '100%' }}>
                <LightTooltip
                  title={
                    <Typography variant='body3' color={zeplinColor.Neutral1}>
                      {normalizedCategoryLabel}
                    </Typography>
                  }
                  placement='bottom-start'
                  style={{
                    backgroundColor: zeplinColor.BasicBlack,
                    borderRadius: '8px',
                    position: 'relative',
                  }}
                >
                  <Chip
                    sx={{
                      backgroundColor: categoryChipColor,
                      borderRadius: '50px',
                      height: '24px',
                      padding: '5px 12px',
                      overflow: 'hidden',
                    }}
                    label={
                      <Typography
                        variant='label1'
                        color={zeplinColor.Neutral1}
                        sx={{
                          textAlign: 'center',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {normalizedCategoryLabel}
                      </Typography>
                    }
                  />
                </LightTooltip>
              </Box>
            )}
          </Box>
        </Box>

        {loading ? (
          <Skeleton
            variant='rounded'
            sx={{
              width: '100%',
              height: 16,
            }}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Typography variant='label1' color={zeplinColor.BasicBlack}>
              Health
            </Typography>
            <LinearProgress
              variant='determinate'
              value={healthScore}
              sx={{
                flex: 1,
                height: 8,
                borderRadius: '999px',
                backgroundColor: zeplinColor.Neutral4,
                '& .MuiLinearProgress-bar': {
                  borderRadius: '999px',
                  backgroundColor: getHealthBarColor(healthScore),
                },
              }}
            />
            <Typography variant='label1' sx={{ color: getHealthBarColor(healthScore) }}>
              {healthScore}%
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: '12px' }}>
          {metricItems.map((metric) => (
            <LightTooltip
              title={
                <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                  {metric.value}
                </Typography>
              }
              placement='bottom-end'
              style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
            >
              <Box
                key={metric.label}
                sx={{
                  borderRadius: '8px',
                  padding: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  backgroundColor: zeplinColor.Neutral3,
                  flex: 1,
                  textAlign: 'center',
                  minWidth: 0,
                }}
              >
                {loading ? (
                  <Skeleton
                    variant='text'
                    sx={{
                      fontSize: theme.typography.body2,
                    }}
                  />
                ) : (
                  <Typography
                    variant={'body2'}
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {renderMetricValue(metric.value, metric.label, metric.isCustomMetric)}
                  </Typography>
                )}
                <Typography variant={'label1'} color={zeplinColor.Neutral8}>
                  {metric.label}
                </Typography>
              </Box>
            </LightTooltip>
          ))}
        </Box>
      </CustomCard>
    </>
  );
};

export default CustomClientCard;
